import React, { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import styled, { css, keyframes } from 'styled-components';
import { X } from 'lucide-react';
import { theme } from '../../tokens';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95) translateY(-10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: ${theme.colors.background.overlay};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[4]};
  z-index: ${theme.zIndices.modal};
  animation: ${fadeIn} ${theme.durations.normal} ${theme.easings.easeOut};

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const modalSizes = {
  sm: css`
    max-width: 400px;
  `,
  md: css`
    max-width: 500px;
  `,
  lg: css`
    max-width: 700px;
  `,
  xl: css`
    max-width: 900px;
  `,
  full: css`
    max-width: calc(100vw - ${theme.spacing[8]});
    max-height: calc(100vh - ${theme.spacing[8]});
  `,
};

const ModalContainer = styled.div`
  background: ${theme.colors.background.elevated};
  border-radius: ${theme.radii['2xl']};
  box-shadow: ${theme.shadows['2xl']};
  width: 100%;
  max-height: calc(100vh - ${theme.spacing[8]});
  display: flex;
  flex-direction: column;
  animation: ${slideIn} ${theme.durations.slow} ${theme.easings.spring};

  ${({ $size }) => modalSizes[$size] || modalSizes.md}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing[6]};
  border-bottom: 1px solid ${theme.colors.border.light};
  flex-shrink: 0;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: ${theme.fontSizes.xl};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.text.primary};
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  background: transparent;
  color: ${theme.colors.text.tertiary};
  cursor: pointer;
  border-radius: ${theme.radii.lg};
  transition: ${theme.transitions.button};

  &:hover {
    background: ${theme.colors.neutral[100]};
    color: ${theme.colors.text.secondary};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${theme.shadows.focus};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const ModalBody = styled.div`
  padding: ${theme.spacing[6]};
  overflow-y: auto;
  flex: 1;

  /* Scrollbar styling */
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${theme.colors.neutral[300]};
    border-radius: ${theme.radii.full};
  }
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${({ $align }) =>
    $align === 'left' ? 'flex-start' : $align === 'center' ? 'center' : 'flex-end'};
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[4]} ${theme.spacing[6]};
  border-top: 1px solid ${theme.colors.border.light};
  flex-shrink: 0;
`;

export const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  footerAlign = 'right',
  size = 'md',
  closeOnOverlayClick = true,
  closeOnEscape = true,
  showCloseButton = true,
}) => {
  const handleEscape = useCallback(
    (e) => {
      if (e.key === 'Escape' && closeOnEscape) {
        onClose?.();
      }
    },
    [closeOnEscape, onClose]
  );

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && closeOnOverlayClick) {
      onClose?.();
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleEscape]);

  if (!isOpen) return null;

  return createPortal(
    <Overlay onClick={handleOverlayClick} role="dialog" aria-modal="true" aria-labelledby="modal-title">
      <ModalContainer $size={size} onClick={(e) => e.stopPropagation()}>
        {(title || showCloseButton) && (
          <ModalHeader>
            {title && <ModalTitle id="modal-title">{title}</ModalTitle>}
            {showCloseButton && (
              <CloseButton onClick={onClose} aria-label="Close modal">
                <X />
              </CloseButton>
            )}
          </ModalHeader>
        )}
        <ModalBody>{children}</ModalBody>
        {footer && <ModalFooter $align={footerAlign}>{footer}</ModalFooter>}
      </ModalContainer>
    </Overlay>,
    document.body
  );
};

Modal.displayName = 'Modal';

export default Modal;
